import React, { useContext, useMemo, useState } from 'react';
import { CenteredContentLayout } from '../../components/containers/CenteredContentLayout';
import { Button } from '../../components/actions/Button';
import { ButtonColorsEnum } from '../../constants/button-colors';
import classes from './DevDashboard.module.scss';
import { AppContext, AppEnvironment } from '../../providers/AppProvider';
import { PanelHeader } from '../../components/containers/Panel/PanelHeader.view';
import { Panel } from '../../components/containers/Panel';
import {
  CreatePostStatsMutation,
  CreatePostStatsMutationVariables,
  GetSocialProfilesQuery,
  useCreateFollowersMutation,
  useCreatePostStatsMutation,
  useGetSocialProfilesQuery,
  useSetSocialProfileToConnectedMutation,
  useSetSocialProfileToDisconnectedMutation,
  useClearThirdPartyPostCacheMutation,
  StartOfWeekFormatSetting,
  useGenerateQueueContentMutation,
  useChangeOrganizationPlanMutation,
  ChangeOrganizationPlanMutation,
  ChangeOrganizationPlanMutationVariables
} from '../../models/graphql';
import { showToast, SystemToast } from '../../helpers/showToast';
import { MutationFunction } from '@apollo/client';
import { OperationVariables } from '@apollo/client/core';
import { useRequiredQueries } from '../../hooks/useRequiredQueries';
import { profilesHelpers } from '../Settings/Profiles/Profiles.helpers';
import { SocialProfile } from '../../components/socialProfiles/SocialProfile';
import { DevDebugContext } from '../../providers/DevDebugProvider';
import { Link } from 'react-router-dom';
import { FormModal } from '../../components/containers/FormModal';
import { RegisterOptions, useForm, UseFormReturn, useWatch, ValidateResult } from 'react-hook-form';
import { CalendarPicker } from '../../components/actions/CalendarPicker';
import { FormOptions, InputTypes } from '../../constants/form';
import { format, subMonths, subYears } from 'date-fns';
import { Input } from '../../components/forms/Input';
import { SubmitErrorHandler, SubmitHandler } from 'react-hook-form/dist/types/form';
import { DateStringFormat } from '../../constants/time-formats';
import { SocialProfileSelector } from '../../components/socialProfiles/SocialProfileSelector';
import { isArray } from 'lodash';
import { MappedProfileType } from '../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { Select } from '../../components/forms/Select';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import { Table } from '../../components/containers/Table';
import { useUserData } from '../../hooks/useUserData';

interface GenerateAnalyticsFormInput {
  startDate: Date;
  endDate: Date;
  profiles: Array<string | false>;
  minPostsPerDay: string;
  maxPostsPerDay: string;
  socialProfileIds: Array<string>;
}

const GenerateAnalyticsModal = (props: {
  formInstance: UseFormReturn;
  socialProfiles: GetSocialProfilesQuery['workspace']['socialProfiles'][number][];
  onSubmit: SubmitHandler<GenerateAnalyticsFormInput>;
  onClose: CallableFunction;
}) => {
  const formInstance = props.formInstance;
  const defaultStartDate = useMemo(() => subMonths(new Date(), 6), []);
  const defaultEndDate = useMemo(() => new Date(), []);
  const maxPostsPerDayValue = useWatch({ control: formInstance.control, name: 'maxPostsPerDay' });

  const minPostsPerDayRules: RegisterOptions = {
    required: 'This is required',
    validate: (value): ValidateResult => {
      if (value < 0) {
        return 'Value must be equal to or greater than 0';
      }
      if (value > 5) {
        return 'Value must be equal to or less than 5';
      }
      if (!isNaN(parseInt(maxPostsPerDayValue)) && value > maxPostsPerDayValue) {
        return 'Min posts per day must not be higher than max posts per day';
      }
    }
  };

  const maxPostsPerDayRules: RegisterOptions = {
    required: 'This is required',
    validate: (value): ValidateResult => {
      if (value < 0) {
        return 'Value must be equal to or greater than 0';
      }
      if (value > 5) {
        return 'Value must be equal to or less than 5';
      }
    }
  };

  const onSubmit: SubmitHandler<GenerateAnalyticsFormInput> = data => {
    props.onSubmit(data);
  };

  const onError: SubmitErrorHandler<GenerateAnalyticsFormInput> = errors => {
    console.error(errors);
  };

  const socialProfilesRules: RegisterOptions = {
    validate: value => {
      return isArray(value) && value.filter(v => v).length ? undefined : 'You must select at least 1 profile';
    }
  };

  const profiles: MappedProfileType[] = useMemo(() => {
    const socialProfiles = props.socialProfiles || [];

    return socialProfiles.map(profile => ({
      ...profile,
      mentionType: profilesHelpers.getMentionType(profile)
    }));
  }, [props.socialProfiles]);

  return (
    <FormModal
      showCloseIcon={true}
      formInstance={formInstance}
      loading={false}
      onClose={props.onClose}
      onSubmit={onSubmit}
      onError={onError}
      size={'small'}
      submitText={'Generate'}
      title={'Generate Past Posts + Stats'}>
      <div>
        <div className={`padding-bottom-small-20`}>
          <CalendarPicker
            label={'Start Date'}
            name={'startDate'}
            useFormInstance={formInstance}
            date={defaultStartDate}
            inputSize={'large'}
            rules={{ required: 'This is required' }}
            calendarProps={{ dateFormat: 'M dd, yy', minDate: subYears(new Date(), 3), maxDate: new Date() }}
            startDay={StartOfWeekFormatSetting.MONDAY}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <CalendarPicker
            label={'End Date'}
            name={'endDate'}
            useFormInstance={formInstance}
            inputSize={'large'}
            date={defaultEndDate}
            rules={{ required: 'This is required' }}
            calendarProps={{ dateFormat: 'M dd, yy', minDate: subYears(new Date(), 3), maxDate: new Date() }}
            startDay={StartOfWeekFormatSetting.MONDAY}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <SocialProfileSelector
            rules={socialProfilesRules}
            label={'Social Profiles'}
            usePinterestBoard={false}
            groupByProfile={false}
            selectItems={profiles}
            useFormInstance={formInstance}
            formFieldName={'socialProfileIds'}
            disabled={props.socialProfiles === undefined}
            disabledProfileIds={[]}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <Input
            formInstance={formInstance}
            name={'minPostsPerDay'}
            type={InputTypes.number}
            value={'0'}
            rules={minPostsPerDayRules}
            size={'large'}
            label={'Min posts per day'}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <Input
            formInstance={formInstance}
            name={'maxPostsPerDay'}
            type={InputTypes.number}
            value={2}
            rules={maxPostsPerDayRules}
            size={'large'}
            label={'Max posts per day'}
          />
        </div>
      </div>
    </FormModal>
  );
};

const MutationsView = (): JSX.Element => {
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [loadingMutationName, setLoadingMutationName] = useState('');
  const [loading, setLoading] = useState(false);
  const { selectedWorkspaceId } = useContext(AppContext);
  const { selectedOrganization } = useSelectedOrganization();
  const socialProfilesQueryResult = useGetSocialProfilesQuery({ variables: { workspaceId: selectedWorkspaceId } });
  const [createPostStatsMutation] = useCreatePostStatsMutation();
  const [generateQueueContentMutation] = useGenerateQueueContentMutation({
    variables: { workspaceId: selectedWorkspaceId },
    context: { timeout: 120000 }
  });
  const [createFollowersMutation] = useCreateFollowersMutation({ variables: { workspaceId: selectedWorkspaceId } });
  const [setProfileToDisconnectedMutation] = useSetSocialProfileToDisconnectedMutation();
  const [setProfileToConnectedMutation] = useSetSocialProfileToConnectedMutation();
  const [clearThirdPartyPostCacheMutation] = useClearThirdPartyPostCacheMutation();
  const [changeOrganizationPlanMutation] = useChangeOrganizationPlanMutation();
  useRequiredQueries([socialProfilesQueryResult]);
  const socialProfiles = socialProfilesQueryResult.data?.workspace.socialProfiles;
  const formInstance: UseFormReturn = useForm({
    ...FormOptions,
    defaultValues: {}
  });

  const runMutation = <TData, TVariables extends OperationVariables>(
    loadingName: string,
    mutation: MutationFunction<TData, TVariables>,
    variables?: TVariables
  ) => {
    showToast({
      detail: '',
      severity: 'info',
      summary: 'Running "' + loadingName + '"'
    });
    setLoadingMutationName(loadingName);
    setLoading(true);
    const result = mutation(variables ? { variables } : {});
    result
      .then(d =>
        showToast({
          severity: 'success',
          summary: 'Finished "' + loadingName + '"',
          detail: <div style={{ whiteSpace: 'pre-line' }}>{Object.values(d.data || {})}</div>,
          sticky: true
        })
      )
      .catch(error => {
        const detail = error.networkError?.result?.errors?.[0]?.extensions?.['violations'] || [].join('\n');
        showToast({
          severity: 'error',
          summary: 'Error for "' + loadingName + '"',
          detail: <div style={{ whiteSpace: 'pre-line' }}>{detail || error.message || ''}</div>,
          sticky: true
        });
      })
      .finally(() => setLoading(false));
    return result;
  };

  const selectedPlanName =
    selectedOrganization?.plan.name.toLocaleLowerCase() === 'free trial'
      ? 'trial'
      : selectedOrganization?.plan.name.toLocaleLowerCase();
  return (
    <Panel>
      <PanelHeader title={'Mutations' + (loading ? ' - Running: "' + loadingMutationName + '"...' : '')} />
      <Panel>
        <PanelHeader title={'Analytics'} />
        <div className={'grid-x grid-margin-x'}>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-26'}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'medium'}
              disabled={loading || !socialProfiles}
              color={ButtonColorsEnum.primary}
              action={() => {
                setShowAnalyticsModal(true);
              }}>
              Generate Post Stat Analytics
            </Button>
            <p className={'margin-top-small-2 no-b-m'}>
              This will generate published posts & stats for all connected profiles
            </p>
          </div>
          {showAnalyticsModal && !!socialProfiles && (
            <GenerateAnalyticsModal
              formInstance={formInstance}
              socialProfiles={socialProfiles}
              onSubmit={values => {
                const socialProfileIds = values.socialProfileIds.filter(Boolean);
                runMutation<CreatePostStatsMutation, CreatePostStatsMutationVariables>(
                  'Generate Post Stat Analytics',
                  createPostStatsMutation,
                  {
                    workspaceId: selectedWorkspaceId,
                    startDate: format(values.startDate, DateStringFormat),
                    endDate: format(values.endDate, DateStringFormat),
                    minPostsPerDay: Number.parseInt(values.minPostsPerDay),
                    maxPostsPerDay: Number.parseInt(values.maxPostsPerDay),
                    socialProfileIds: socialProfileIds
                  }
                );
                setShowAnalyticsModal(false);
              }}
              onClose={() => setShowAnalyticsModal(false)}
            />
          )}
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-26'}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'medium'}
              disabled={loading}
              color={ButtonColorsEnum.primary}
              action={() => {
                runMutation('Generate Follower Analytics', createFollowersMutation);
              }}>
              Generate Follower Analytics
            </Button>
            <p className={'margin-top-small-2 no-b-m'}>This will generate followers for all connected profiles</p>
          </div>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-26'}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'medium'}
              disabled={loading}
              color={ButtonColorsEnum.primary}
              action={() => {
                runMutation('Generate Queue Content', generateQueueContentMutation);
              }}>
              Generate Queue Content
            </Button>
            <p className={'margin-top-small-2 no-b-m'}>
              This will generate a bunch of different types of posts (upcoming, draft, failed, etc)
            </p>
          </div>
        </div>
      </Panel>
      <Panel>
        <PanelHeader title={'Social Profiles'} />
        {(socialProfilesQueryResult.loading && <h3>Loading Profiles...</h3>) ||
          socialProfiles?.map(socialProfile => (
            <div key={`social-profile-${socialProfile.id}`}>
              <div className={'grid-x grid-margin-x'}>
                <div className={'cell small-12 margin-bottom-small-16'}>
                  <SocialProfile
                    size={'large'}
                    socialProfile={profilesHelpers.remapSocialProfile(socialProfile, false, false, false)}
                    label={true}
                    disconnected={!socialProfile.connected}
                  />
                </div>
                <div className={'cell small-12'}>
                  <Button
                    type={'button'}
                    variant={'solid'}
                    size={'xSmall'}
                    disabled={loading}
                    color={ButtonColorsEnum.primary}
                    action={() => {
                      if (socialProfile.connected) {
                        runMutation(
                          `Set ${socialProfile.displayName} to disconnected`,
                          setProfileToDisconnectedMutation,
                          { socialProfileId: socialProfile.id }
                        ).then(() => {
                          setLoading(true);
                          socialProfilesQueryResult.refetch().finally(() => setLoading(false));
                        });
                      } else {
                        runMutation(`Set ${socialProfile.displayName} to connected`, setProfileToConnectedMutation, {
                          socialProfileId: socialProfile.id
                        }).then(() => {
                          setLoading(true);
                          socialProfilesQueryResult.refetch().finally(() => setLoading(false));
                        });
                      }
                    }}>
                    {socialProfile.connected ? 'Set to disconnected' : 'Set to connected'}
                  </Button>
                </div>
              </div>
              <hr />
            </div>
          ))}
      </Panel>
      <Panel>
        <PanelHeader title={'Cache'} />
        <div className={'grid-x grid-margin-x'}>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
            <Button
              type={'button'}
              variant={'solid'}
              size={'medium'}
              disabled={loading}
              color={ButtonColorsEnum.primary}
              action={() => {
                runMutation('Clear Third Party Post Cache', clearThirdPartyPostCacheMutation);
              }}>
              Clear Third Party Post Cache
            </Button>
          </div>
        </div>
      </Panel>
      <Panel>
        <PanelHeader title={'Plan & Subscription'} />
        <div className={'grid-x grid-margin-x'}>
          <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
            <Select
              useFormInstance={formInstance}
              selectedValue={selectedPlanName}
              items={[
                { value: 'trial', label: 'Trial' },
                { value: 'solo', label: 'Solo' },
                { value: 'business', label: 'Business' },
                { value: 'agency', label: 'Agency' }
              ]}
              action={(val, callback) => {
                if (typeof val === 'string' && typeof selectedOrganization?.id === 'string') {
                  runMutation<ChangeOrganizationPlanMutation, ChangeOrganizationPlanMutationVariables>(
                    'Change Plan',
                    changeOrganizationPlanMutation,
                    {
                      organizationId: selectedOrganization.id,
                      planName: val
                    }
                  )
                    .then(() => callback(true))
                    .catch(() => callback(false));
                }
              }}
              label={'Change your Organization Plan'}
              name={'plan_select'}
              size={'medium'}
              hideDefaultSelectOption
              rules={{}}
            />
          </div>
        </div>
      </Panel>
    </Panel>
  );
};

const ToastsView = (): JSX.Element => {
  const {
    appConfig: { environment }
  } = useContext(AppContext);
  const [_, setRerender] = useState({});
  const rerender = () => {
    setRerender({});
  };

  if (environment !== AppEnvironment.DEV) {
    return <></>;
  }

  const createButton = (
    severity: SystemToast['severity'],
    detail: SystemToast['detail'],
    buttonText: string,
    randomSummary = false
  ): JSX.Element => {
    let color: ButtonColorsEnum = ButtonColorsEnum.primary;
    let summary = '';
    switch (severity) {
      case 'info':
        color = ButtonColorsEnum.primary;
        summary = 'Info';
        break;
      case 'success':
        color = ButtonColorsEnum.secondary;
        summary = 'Success';
        break;
      case 'warn':
        color = ButtonColorsEnum.grey;
        summary = 'Warning';
        break;
      case 'error':
        color = ButtonColorsEnum.alert;
        summary = 'Error';
        break;
    }
    if (randomSummary) {
      summary += ' ' + Math.random();
    }
    return (
      <div className={'cell shrink'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={color}
          action={() => {
            showToast({
              summary,
              detail,
              severity
            });
            rerender();
          }}>
          {buttonText}
        </Button>
      </div>
    );
  };

  const detailStandard = 'This is a toast';
  const detailExternalLink = (
    <div>
      This is an info toast with an external link{' '}
      <a href={'https://cats.com'} target={'_blank'} rel={'noreferrer'}>
        click me
      </a>
    </div>
  );
  const detailInternalLink = (
    <div>
      This is an info toast with an internal link <Link to={'/settings'}>click me</Link>
    </div>
  );

  return (
    <Panel>
      <PanelHeader title={'Toasts'} />
      <div className={'grid-x grid-margin-x grid-margin-y'}>
        {createButton('info', detailStandard, 'Info')}
        {createButton('info', detailExternalLink, 'Info w/external link')}
        {createButton('info', detailInternalLink, 'Info w/internal link')}
      </div>
      <div className={'grid-x grid-margin-x grid-margin-y'}>
        {createButton('success', detailStandard, 'Success')}
        {createButton('success', detailExternalLink, 'Success w/external link')}
        {createButton('success', detailInternalLink, 'Success w/internal link')}
      </div>
      <div className={'grid-x grid-margin-x grid-margin-y'}>
        {createButton('warn', detailStandard, 'Warning')}
        {createButton('warn', detailExternalLink, 'Warning w/external link')}
        {createButton('warn', detailInternalLink, 'Warning w/internal link')}
      </div>
      <div className={'grid-x grid-margin-x grid-margin-y'}>
        {createButton('error', detailStandard, 'Error')}
        {createButton('error', detailExternalLink, 'Error w/external link')}
        {createButton('error', detailInternalLink, 'Error w/internal link')}
      </div>
      <p></p>
    </Panel>
  );
};

export const DevDashboardView = (): JSX.Element => {
  const { allowedDevDashboardMutations, allowedDebugMode, debugMode, setDebugMode } = useContext(DevDebugContext);
  const { userData } = useUserData();
  const selectedOrgWorkspace = useSelectedOrganization();

  return (
    <div className={classes.DevDashboard}>
      <CenteredContentLayout hasSidebar={false} wider={true} headerTitle={'Dev Dashboard'}>
        <Panel>
          <PanelHeader title={'General'} />
          {allowedDebugMode && (
            <>
              <Panel>
                <PanelHeader title={'Debug Mode: ' + (debugMode ? 'On' : 'Off')} />
                <div className={'grid-x grid-margin-x'}>
                  <div className={'cell small-12 tablet-portrait-6 margin-bottom-small-16'}>
                    <Button
                      type={'button'}
                      variant={'solid'}
                      size={'small'}
                      color={ButtonColorsEnum.primary}
                      action={() => {
                        setDebugMode(!debugMode);
                      }}>
                      Turn debug mode {debugMode ? 'off' : 'on'}
                    </Button>
                  </div>
                </div>
              </Panel>
              <Panel>
                <PanelHeader title={'Basic Information'} />
                <div className={'grid-x grid-margin-x'}>
                  <div className={'cell small-12 tablet-portrait-8 margin-bottom-small-16'}>
                    <div className={classes.TableContainer}>
                      <Table tableStyle={'stacked_scrolling'}>
                        <>
                          <thead>
                            <tr>
                              <th>User ID</th>
                              <th>Organization ID</th>
                              <th>Workspace ID</th>
                              <th>Organization Legacy Member ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{userData?.id}</td>
                              <td>{selectedOrgWorkspace.selectedOrganization?.id}</td>
                              <td>{selectedOrgWorkspace.selectedWorkspace?.id}</td>
                              <td>{selectedOrgWorkspace.selectedOrganization?.legacyMemberId}</td>
                            </tr>
                          </tbody>
                        </>
                      </Table>
                    </div>
                  </div>
                </div>
              </Panel>
            </>
          )}
        </Panel>
        {allowedDevDashboardMutations && <MutationsView />}
        <ToastsView />
      </CenteredContentLayout>
    </div>
  );
};
